import * as React from "react";
import moment from "moment-timezone";
import {
  Catalog16,
  ChartMultiLine16,
  Currency16,
  ListChecked16,
  PinFilled16,
  Redo16,
  SatelliteRadar16,
  ArrowUp16,
} from "@carbon/icons-react";

import {
  AppraisalKinds,
  ArchiveKinds,
  CloneKinds,
  ConditionKinds,
  DeclarationsKinds,
  DocmagicKinds,
  FeeChangeKinds,
  HurdleNoteKinds,
  KahunaMessageKinds,
  LobLetterKinds,
  PointsAndCreditsKinds,
  SSNKinds,
  SandboxKinds,
  WorksheetKinds,
  FieldUpdateKinds,
  ContactUpdatedKinds,
} from "web_modules/Events/types";

const FontAwesome = require("react-fontawesome");

const styles = require("./styles.module.scss");

export const formatTime = (t: string) => moment(t).tz(moment.tz.guess()).format("MM/DD/YY HH:mm z");

const isKind = (kinds, k: string): boolean => Object.values(kinds).includes(k);

export const isAppraisalKind = (k: string): boolean => isKind(AppraisalKinds, k);
export const isArchivedKind = (k: string): boolean => isKind(ArchiveKinds, k);
export const isCloneKind = (k: string): boolean => isKind(CloneKinds, k);
export const isConditionKind = (k: string): boolean => isKind(ConditionKinds, k);
export const isDeclarationsKind = (k: string): boolean => isKind(DeclarationsKinds, k);
export const isDocmagicKind = (k: string): boolean => isKind(DocmagicKinds, k);
export const isFeeChangeKind = (k: string): boolean => isKind(FeeChangeKinds, k);
export const isHurdleNoteKind = (k: string): boolean => isKind(HurdleNoteKinds, k);
export const isKahunaMessageKind = (k: string): boolean => isKind(KahunaMessageKinds, k);
export const isLobLetterKind = (k: string): boolean => isKind(LobLetterKinds, k);
export const isPointsAndCreditsKind = (k: string): boolean => isKind(PointsAndCreditsKinds, k);
export const isSandboxKind = (k: string): boolean => isKind(SandboxKinds, k);
export const isSSNKind = (k: string): boolean => isKind(SSNKinds, k);
export const isWorksheetKind = (k: string): boolean => isKind(WorksheetKinds, k);
export const isFieldUpdateKind = (k: string): boolean => isKind(FieldUpdateKinds, k);
export const isContactUpdatedKind = (k: string): boolean => isKind(ContactUpdatedKinds, k);

const defaultIcon = "circle-thin";

const slackIcons = {
  "4506-t": "history",
  "account created": "user-plus",
  appraisal: "home",
  assets: "dollar",
  "borrower auth": "thumbs-o-up",
  "borrower comment": "comment-o",
  "change of circumstance": "refresh",
  closingcorp: "briefcase",
  "credit application complete": "key",
  "credit report": "bar-chart",
  docmagic: "magic",
  "document deleted": "trash-o",
  "document uploaded": "file-text-o ",
  "email confirmation": "check-circle-o",
  "email delivery failure": "exclamation-triangle",
  "homelight lead": "lightbulb-o",
  "loan decision": "cog",
  "loan status": "cog",
  lsfm: "certificate",
  "message from borrower": "envelope-o",
  "new conditional approval": "rocket",
  plaid: "university",
  "product selected": "bullseye",
  "respa application complete": "bolt",
  "selected scenario": "sliders",
  "sending email": "bullhorn",
  "sent a message": "paper-plane-o",
  "settlement agent": "user-secret",
  "social security number": "id-card-o",
};

export const titleToIcon = (t: string): string => {
  if (!t) {
    return defaultIcon;
  }

  const subs = Object.keys(slackIcons);
  for (let i = 0, l = subs.length; i < l; i++) {
    if (t.toLowerCase().includes(subs[i])) {
      return slackIcons[subs[i]];
    }
  }

  return defaultIcon;
};

const eventIcons = {
  appraisal: "home",
  archive: "archive",
  clone: "clone",
  declarations: "edit",
  docmagic: "magic",
  lob: "futbol-o", // get it? ^_^
  ssn: "id-card-o",
  worksheet: "database",
};

export const kindToIcon = (k: string, isPinned: boolean) => {
  if (isPinned) {
    return <PinFilled16 className={styles.carbonIcon} />;
  }

  // going forward, standardize on icons from Carbon (here) and not FontAwesome (below)
  if (isConditionKind(k)) {
    return <ListChecked16 className={styles.carbonIcon} />;
  }
  if (isFeeChangeKind(k)) {
    return <Currency16 className={styles.carbonIcon} />;
  }
  if (isHurdleNoteKind(k)) {
    return <Catalog16 className={styles.carbonIcon} />;
  }
  if (isKahunaMessageKind(k)) {
    return <SatelliteRadar16 className={styles.carbonIcon} />;
  }
  if (isPointsAndCreditsKind(k)) {
    return <ChartMultiLine16 className={styles.carbonIcon} />;
  }
  if (isSandboxKind(k)) {
    return <Redo16 className={styles.carbonIcon} />;
  }
  if (isFieldUpdateKind(k)) {
    return <ArrowUp16 className={styles.carbonIcon} />;
  }

  let name: string;

  if (isPinned) {
    throw new Error("unreached - handled above as carbon icon");
  } else if (isAppraisalKind(k)) {
    name = eventIcons.appraisal;
  } else if (isArchivedKind(k)) {
    name = eventIcons.archive;
  } else if (isCloneKind(k)) {
    name = eventIcons.clone;
  } else if (isConditionKind(k)) {
    throw new Error("unreached - handled above as carbon icon");
  } else if (isDeclarationsKind(k)) {
    name = eventIcons.declarations;
  } else if (isDocmagicKind(k)) {
    name = eventIcons.docmagic;
  } else if (isHurdleNoteKind(k)) {
    throw new Error("unreached - handled above as carbon icon");
  } else if (isKahunaMessageKind(k)) {
    throw new Error("unreached - handled above as carbon icon");
  } else if (isLobLetterKind(k)) {
    name = eventIcons.lob;
  } else if (isSSNKind(k)) {
    name = eventIcons.ssn;
  } else if (isWorksheetKind(k)) {
    name = eventIcons.worksheet;
  } else if (isFieldUpdateKind(k)) {
    throw new Error("unreached - handled above as carbon icon");
  } else {
    name = defaultIcon;
  }

  return <FontAwesome className={styles.icon} name={name} fixedWidth />;
};
